import { pathOr } from '../helpers/utils';
import { dateFriendly } from '../helpers/date';
import { priceFormat, priceFromAPI } from '../helpers/price';
import { ORDER_ITEM_TYPE_TITLE } from '../enums/order';

/**
 * transform order
 * @param {Object} order
 * @returns {Object} order info
 */
export const transformOrder = (order = {}) => {
	return {
		id: order.id || null,
		bnnOrderId: order.bnn_order_id || null,
		thunderOrderId: order.thunder_order_id || null,
		orderCode: order.order_number || null,
		orderStatus: order.order_status || null,
		externalOrderNumber: order.external_order_number || null,
		customer: {
			id: pathOr('', ['customer', 'id'])(order),
			firstName: pathOr('', ['customer', 'first_name'])(order),
			lastName: pathOr('', ['customer', 'last_name'])(order),
			phoneNumber: pathOr('', ['customer', 'phone_number'])(order),
			email: pathOr('', ['customer', 'email'])(order),
			customerGroup: {
				id: pathOr('', ['customer', 'customer_group', 'id'])(order),
				name: pathOr('', ['customer', 'customer_group', 'name'])(order),
			},
		},
		channel: {
			name: pathOr(null, ['placed_form', 'channel', 'name'])(order),
		},
		paymentMethod: {
			name: pathOr(null, ['payment', 'payment_method'])(order),
		},
		shippingMethod: {
			name: null,
		},
		paymentStatus: null,
		shipmentStatus: order.shipping_status || null,
		total: priceFormat(priceFromAPI(pathOr(0, ['total', 'total_price'])(order))),
		createdAt: order.created_at ? dateFriendly(order.created_at, 'DD MMM YYYY, HH:mm') : null,
		updatedAt: order.updated_at ? dateFriendly(order.updated_at, 'DD MMM YYYY, HH:mm') : null,
		reconciledAt: order.reconciled_at ? dateFriendly(order.reconciled_at, 'DD MMM YYYY, HH:mm') : null,
		syncedWithThunderAt: order.synced_with_thunder_at ? dateFriendly(order.synced_with_thunder_at, 'DD MMM YYYY, HH:mm') : null,
	};
};

/**
 * transform orders
 * @param {Array} order
 * @returns {Array} order list
 */
export const transformOrders = (orders = []) => {
	return orders.map((item) => transformOrder(item));
};

/**
 * transform order general information
 * @param {Object} order
 * @returns {Object} order info
 */
export const transformOrderGeneralInfo = (order = {}) => {
	const lastSyncedWithItecAt = order && order.commitments && order.commitments.last_synced_with_itec_at ? order.commitments.last_synced_with_itec_at : null;

	return {
		id: order.id || null,
		orderCode: order.order_number || null,
		orderStatus: order.order_status || null,
		thunderOrderId: order.thunder_order_id || null,
		bnnOrderId: order.bnn_order_id || null,
		type: ORDER_ITEM_TYPE_TITLE[pathOr(null, ['type'])(order)],
		externalOrderNumber: order.external_order_number || null,
		customer: {
			id: pathOr('-', ['customer', 'id'])(order),
			firstName: pathOr('', ['customer', 'first_name'])(order),
			lastName: pathOr('', ['customer', 'last_name'])(order),
			phoneNumber: pathOr('-', ['customer', 'phone_number'])(order),
			email: pathOr('-', ['customer', 'email'])(order),
			customerGroup: {
				id: null,
				name: null,
			},
		},
		paymentMethod: {
			name: pathOr('-', ['payment', 'payment_method'])(order),
		},
		shippingMethod: {
			name: null,
		},
		commitments: {
			syncedWithITECAt: lastSyncedWithItecAt ? dateFriendly(lastSyncedWithItecAt, 'DD MMM YYYY, HH:mm:ss') : null,
		},
		paymentStatus: null,
		shippingStatus: order.shipping_status || null,
		total: priceFormat(priceFromAPI(pathOr(0, ['total', 'total_price'])(order))),
		createdAt: order.created_at ? dateFriendly(order.created_at) : '-',
		updatedAt: order.updated_at ? dateFriendly(order.updated_at) : '-',
		lastSyncedAt: order.last_synced_at ? dateFriendly(order.last_synced_at) : '-',
		syncedWithThunderAt: order.synced_with_thunder_at ? dateFriendly(order.synced_with_thunder_at) : '-',
	};
};

/**
 * transform order's product list
 * @param {Object} product list of order
 * @returns {Object} product list
 */
export const transformOrderProductList = (product = {}) => {
	const id = pathOr(null, ['id'])(product);
	const name = pathOr(null, ['product', 'name'])(product);
	const sku = pathOr(null, ['product', 'itec_sku'])(product);
	const type = pathOr(null, ['type'])(product);
	const thumbnail = pathOr(null, ['thumbnail_url'])(product);
	const discount = priceFormat(priceFromAPI(pathOr(null, ['discount'])(product)));
	const priceSRP = '-';
	const priceSelling = priceFormat(priceFromAPI(pathOr(null, ['price_selling'])(product)));
	const vatSubtotal = priceFormat(priceFromAPI(pathOr(null, ['vat_subtotal'])(product)));
	const total = priceFormat(priceFromAPI(pathOr(null, ['total'])(product)));
	const shippingFee = priceFormat(priceFromAPI(pathOr(null, ['shipping_fee'])(product)));
	const quantity = pathOr(0, ['quantity'])(product);

	return {
		id,
		name,
		sku,
		type,
		thumbnail,
		priceSelling,
		priceSRP,
		discount,
		vatSubtotal,
		shippingFee,
		total,
		quantity,
	};
};

/**
 * transform order detail
 * @param {Array} order
 * @returns {Array} order detail
 */
export const transformOrderDetail = (order = {}) => {
	const quantity = order.items && order.items.map((item) => item.quantity)
		.reduce((acc, cur) => {
			return acc + cur;
		}, 0);

	return {
		...transformOrderGeneralInfo(order),
		store: {
			id: pathOr(null, ['placed_form', 'channel', 'id'])(order),
			name: pathOr('-', ['placed_form', 'channel', 'name'])(order),
		},
		note: null,
		items: order.items && order.items.map((item) => transformOrderProductList(item)),
		quantity,
		discount: priceFormat(priceFromAPI(pathOr(null, ['total', 'total_discount'])(order))),
		subtotal: priceFormat(priceFromAPI(pathOr(null, ['total', 'subtotal'])(order))),
		shippingFee: priceFormat(priceFromAPI(pathOr(null, ['total', 'shipping_fee'])(order))),
		// TODO: recheck with BE
		additionalSippingFee: 0,
		total: priceFormat(priceFromAPI(pathOr(null, ['total', 'total_price'])(order))),
	};
};

/**
 * transform order document detail
 * @param {Array} order
 * @returns {Array} order document detail
 */
export const transformOrderDocDetail = (item = {}) => {
	return {
		base64: item.file,
		mimeType: item.mime_type,
		documentType: item.document_type,
	};
};

/**
 * transform order document multiple
 * @param {Object} order
 * @returns {Object} order document multiple
 */
export const transformOrderDocMultiple = (item = {}) => {
	return {
		link: item.link,
	};
};
